const MARKDOWN_PATTERN = /[*_`#[\]()>-]/;

const LIST_PATTERN = /^\s*[-*+]\s/m;
const ORDERED_LIST_PATTERN = /^\s*\d+\.\s/m;
const HEADING_PATTERN = /^\s*#{1,6}\s/m;
const TABLE_SEPARATOR_PATTERN = /^\s*\|.*\|/m;

/**
 * Heuristically determine if a text blob likely contains Markdown syntax.
 *
 * This mirrors existing behaviour in components like the task description renderer
 * so that we only invoke the Markdown pipeline when necessary.
 */
/**
 * Strip common Markdown syntax from a string so it reads as clean plain text.
 * Useful for preview/truncated contexts where rendering is impractical.
 */
export const stripMarkdownSyntax = (text: string): string => {
  if (!text) return '';

  let result = text;
  // Bold / italic: **text**, __text__, *text*, _text_
  result = result.replace(/\*\*(.+?)\*\*/g, '$1');
  result = result.replace(/__(.+?)__/g, '$1');
  result = result.replace(/\*(.+?)\*/g, '$1');
  result = result.replace(/(?<=\s|^)_(.+?)_(?=\s|$|[.,;!?])/g, '$1');
  // Inline code
  result = result.replace(/`([^`]+)`/g, '$1');
  // Links: [text](url) → text
  result = result.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Headings: ### text → text
  result = result.replace(/^#{1,6}\s+/gm, '');
  // Collapse multiple whitespace
  result = result.replace(/\s+/g, ' ');

  return result.trim();
};

export const containsMarkdownSyntax = (
  text: string | null | undefined,
): boolean => {
  if (!text) {
    return false;
  }

  // Check whitespace-dependent patterns on original text first
  // These patterns rely on specific positioning and indentation
  if (
    LIST_PATTERN.test(text) ||
    ORDERED_LIST_PATTERN.test(text) ||
    HEADING_PATTERN.test(text) ||
    TABLE_SEPARATOR_PATTERN.test(text)
  ) {
    return true;
  }

  // Use trimmed text for position-independent checks
  const candidate = text.trim();
  if (!candidate) {
    return false;
  }

  return (
    MARKDOWN_PATTERN.test(candidate) ||
    candidate.includes('\n\n') ||
    candidate.includes('**') ||
    candidate.includes('__') ||
    candidate.includes('`')
  );
};
